<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <BaseAlert
      v-else
      v-bind="recorder"
      slot="middle"
    />
    <ModalConfirm
      v-if="isAbortModalVisible"
      v-bind="abortModal"
      slot="root"
      class="screen__overlay"
      @agree="abortRecording"
      @disagree="hideAbortModal"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';
import audioMixin from '@/mixins/audio';
import timerMixin from '@/mixins/timer';

import ModalConfirm from '@/components/ModalConfirm.vue';

export default {
  name: 'AudioRecordingScreen',
  mixins: [screenMixin, audioMixin, timerMixin],
  components: {
    ModalConfirm,
  },
  data() {
    return {
      isPlaying: false,
      isRecording: false,
      isAbortModalVisible: false,
      shouldAbort: false,
      time: '00:00',
    };
  },
  computed: {
    ...mapState('feedback', [
      'audio',
    ]),
    pendingText() {
      return this.$t('pending.hardware');
    },
    screenCase() {
      if (this.isErrorVisible) {
        return CONSTANTS.STEP_HARDWARE_ERROR;
      }
      if (this.isAudioReady) {
        return CONSTANTS.STEP_AUDIO_READY;
      }
      return null;
    },
    recorder() {
      return {
        icon: {
          name: 'microphone',
          theme: 'primary',
          size: 'l',
        },
        title: {
          text: this.recorderTitleText,
          size: 'l',
        },
        description: {
          text: this.recorderDescriptionText,
          size: 'l',
        },
        actions: this.recorderActions,
      };
    },
    recorderTitleText() {
      if (this.isRecording) return this.$t('titles.recordIsStarted');
      if (this.isAudioReady) return this.$t('titles.recordIsFinish');
      return this.$t('titles.audioRecord');
    },
    recorderDescriptionText() {
      if (this.isRecording || this.isPlaying) {
        const indicator = this.isRecording ? 'stopwatch_has_indicator' : '';
        return `<span class="stopwatch ${indicator}">${this.time}</span>`;
      }
      if (this.isAudioReady) return this.$t('texts.playRecording');
      return this.$t('texts.startRecording');
    },
    recorderActions() {
      if (this.isPlaying) {
        return [
          {
            text: this.$t('actions.rewrite'),
            action: this.startNewRecording,
            view: 'outlined',
          },
          {
            text: this.$t('actions.continue'),
            action: this.goToAudioSendScreen,
          },
        ];
      }
      if (this.isRecording) {
        return [
          {
            text: this.$t('actions.abort'),
            action: this.showAbortModal,
            view: 'outlined',
          },
          {
            text: this.$t('actions.finish'),
            action: this.stopRecording,
          },
        ];
      }
      if (this.isAudioReady) {
        return [
          {
            text: this.$t('actions.rewrite'),
            action: this.startNewRecording,
            view: 'outlined',
          },
          {
            text: this.$t('actions.listen'),
            action: this.playAudio,
            view: 'outlined',
          },
          {
            text: this.$t('actions.continue'),
            action: this.goToAudioSendScreen,
          },
        ];
      }
      return [
        {
          text: this.$t('actions.start'),
          action: this.startRecording,
        },
      ];
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badHardware'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.operationNotPossible'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
        ],
      };
    },
    abortModal() {
      return {
        title: {
          text: this.$t('titles.confirmAbortRecording'),
          size: 'l',
          isNormal: true,
          isCenter: true,
        },
        agreeText: this.$t('actions.yesAbort'),
        disagreeText: this.$t('actions.noContinue'),
        isInversed: true,
      };
    },
    isAudioReady() {
      return Boolean(this.audio);
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isRecording && !this.shouldAbort) {
      this.showAbortModal();
    } else {
      next();
    }
  },
  created() {
    this.clearAudio();
    this.showPending(0);
    this.startNewRecording();
    this.initActions([
      [CONSTANTS.ACTION_REMAKE, this.startNewRecording],
      [CONSTANTS.ACTION_PLAY, this.playAudio],
      [CONSTANTS.ACTION_CONTINUE, this.goToAudioSendScreen],
    ]);
  },
  methods: {
    ...mapMutations('feedback', [
      'setAudio',
      'clearAudio',
    ]),
    startNewRecording() {
      if (this.isRecording) return;
      if (this.isPlaying) {
        this.stopAudioPlaying();
      }
      this.startRecording();
    },
    startRecording() {
      this.startAudioRecording(); // функция из миксина
    },
    stopRecording() {
      this.stopAudioRecording(); // функция из миксина
    },
    playAudio() {
      if (!this.isAudioReady) return;
      this.startAudioPlaying(this.audio); // функция из миксина
    },
    onAudioRecordStart() {
      // Этот колбек будет вызван из миксина, когда начнется запись
      this.isRecording = true;
      this.startTimer();
      this.hidePending();
    },
    onAudioRecordStop(audio) {
      // Этот колбек будет вызван из миксина, когда запись остановлена
      this.isRecording = false;
      this.stopTimer();
      this.setAudio(audio);
    },
    onAudioPlayingStart() {
      // Этот колбек будет вызван из миксина, когда начнется проигрывание записи
      this.isPlaying = true;
      this.startTimer();
    },
    onAudioPlayingStop() {
      // Этот колбек будет вызван из миксина, когда закончится проигрывание записи
      this.isPlaying = false;
      this.stopTimer();
    },
    onTimerUpdate(time) {
      // Этот колбек будет вызван из миксина
      if (this.time !== time) {
        this.time = time;
      }
    },
    onAudioRecordingError() {
      // Этот колбек будет вызван из миксина, если прилетит ошибка записи
      this.showError();
    },
    abortRecording() {
      this.setAbortFlag();
      this.goToBackScreen();
    },
    setAbortFlag() {
      this.shouldAbort = true;
    },
    beforeInteractionStopped() {
      this.setAbortFlag();
    },
    showAbortModal() {
      this.isAbortModalVisible = true;
    },
    hideAbortModal() {
      this.isAbortModalVisible = false;
    },
    goToAudioSendScreen() {
      if (!this.isAudioReady) return;
      this.$router.push(this.$router.options.paths.audioSend);
    },
  },
};
</script>
